import React, { useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { INVALID_CODES } from '@/core/validation';
import { toSpliced } from '@/util/arrayUtil';
import { isUrl } from '@/util/stringUtil';
import { isValidBannerTextWhiteList } from '@/util/helper/webchatThemeHelper';
import { useScenarioId } from '@/hook';
import { successToast } from '@/component/Toast';
import { TextInputGroup } from '@/component/input';
import { AddIcon, DeleteIcon, PasteIcon } from '@/assets/svgs/icons';

import { WebchatEmbeddedFormProps } from ".";


const InstallationInputGroup: React.FC<WebchatEmbeddedFormProps> = (props) => {
    const { embeddedSettings, legend, onChange } = props;

    const intl = useIntl();

    const scenarioId = useScenarioId();

    const id = useId();
    const labelId = `${id}-label`;
    const errorId = `${id}-error`;
    const noteId = `${id}-note`;

    const whitelist = embeddedSettings.buttonShowUrlWhiteList?.split(",").filter(url => url.trim() !== "") ?? [];

    const whitelistValidation = useMemo(() => isValidBannerTextWhiteList(embeddedSettings.buttonShowUrlWhiteList), [embeddedSettings.buttonShowUrlWhiteList]);

    const getInvalidText = (code?: string) => {
        if (!code) {
            return undefined;
        }
        switch (code) {
            case INVALID_CODES.INVALID_FORMAT:
                return intl.formatMessage({ id: "i09083" });
            case INVALID_CODES.DUPLICATE:
                return intl.formatMessage({ id: "i09087" });
            default:
                return undefined;
        }
    };

    const installationCode =
        '<script type="text/javascript" '
            + 'charset="utf-8" '
            + 'id="swing-chat-plugin" '
            + `data-id="${scenarioId}" `
            + 'src="https://webchat.swing.chat/swing-chat-plugin.js"></script>';

    const onClickCopyInstallationCode = (e: React.MouseEvent) => {
        e.preventDefault();

        navigator.clipboard.writeText(installationCode)
            .then(() => {
                successToast(intl.formatMessage({ id: "i09080" }));
            });
    };

    const onChangeWhitelist = (newWhitelist: string[]) => {
        const newWhitelistStr = newWhitelist.join(",");
        onChange({
            ...embeddedSettings,
            buttonShowUrlWhiteList: newWhitelistStr,
            mobile: {
                ...embeddedSettings.mobile!,
                buttonShowUrlWhiteList: newWhitelistStr,
            }
        });
    };

    return (
        <fieldset className="flex flex-col gap-4">
            <legend className="sr-only">
                {legend}
            </legend>

            <fieldset className="flex flex-col">
                <legend className="h-input-default flex items-center field-label">
                    {intl.formatMessage({ id: "i09081" })}
                </legend>
                <div className="flex flex-col gap-1">
                    {!whitelistValidation.isValid && (
                        <span id={errorId}
                              className="text-invalid"
                        >
                            {getInvalidText(whitelistValidation.code)}
                        </span>
                    )}
                    <WhitelistInputGroup whitelist={whitelist}
                                         legend={intl.formatMessage({ id: "i09081" })}
                                         aria-describedby={noteId}
                                         aria-errormessage={!whitelistValidation.isValid ? errorId : undefined}
                                         onChange={onChangeWhitelist}
                    />
                    <div role="note"
                         id={noteId}
                         className="p-4 flex flex-col gap-3 border border-black-200 rounded-[8px] bg-black-100 text-[12px] font-[400] text-black-500"
                    >
                        <div className="leading-none">
                            {intl.formatMessage({ id: "i09084" })}
                        </div>
                        <div className="whitespace-pre-wrap leading-[1.5]">
                            {intl.formatMessage({ id: "i09085" })}
                        </div>
                    </div>
                </div>
            </fieldset>

            <div className="flex flex-col">
                <div id={labelId}
                     className="h-input-default flex items-center gap-1 field-label"
                >
                    {intl.formatMessage({ id: "i09086" })}
                    <button aria-label={intl.formatMessage({ id: "b0062" })}
                            onClick={onClickCopyInstallationCode}
                    >
                        <PasteIcon className="size-3.5" />
                    </button>
                </div>
                <code aria-labelledby={labelId}
                      className="p-4 rounded-[8px] bg-black-200 text-[12px] font-[400] text-black-500"
                >
                    {installationCode}
                </code>
            </div>
        </fieldset>
    );
};

interface WhitelistInputGroupProps {
    whitelist: string[];
    legend: string;
    "aria-describedby"?: string;
    "aria-errormessage"?: string;
    onChange: (value: string[]) => void;
}

const WhitelistInputGroup: React.FC<WhitelistInputGroupProps> = (props) => {
    const {
        whitelist,
        legend,
        "aria-errormessage": ariaErrorMessage,
        "aria-describedby": ariaDescribedBy,
        onChange
    } = props;

    const intl = useIntl();

    const [inputValue, setInputValue] = useState("");

    const onSubmit = () => {
        if (isInvalid || whitelist.includes(inputValue)) {
            return;
        }
        onChange([...whitelist, inputValue]);
        setInputValue("");
    };

    const isInvalid = !!inputValue && (!isUrl(inputValue) || whitelist.includes(inputValue));
    const invalidText = useMemo(() => {
        if (!isInvalid) {
            return;
        }
        return !isUrl(inputValue) ? intl.formatMessage({ id: "i09083" }) : intl.formatMessage({ id: "i09087" });
    }, [isInvalid, inputValue, intl]);

    return (
        <fieldset className="flex flex-col gap-1"
                  aria-describedby={ariaDescribedBy}
        >
            <legend className="sr-only">
                {legend}
            </legend>
        
            <div className="flex gap-2 items-center">
                <TextInputGroup value={inputValue}
                                placeholder={intl.formatMessage({ id: "i09082" })}
                                invalid={isInvalid}
                                invalidText={invalidText}
                                aria-errormessage={ariaErrorMessage}
                                onChange={value => setInputValue(value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        onSubmit();
                                    }
                                }}
                />
                <button disabled={isInvalid || !inputValue.trim()}
                        aria-label={intl.formatMessage({ id: "b0009" })}
                        className="label-input-grid-label text-chat42 disabled:text-black-200"
                        onClick={onSubmit}
                >
                    <AddIcon className="size-4" />
                </button>
            </div>

            {whitelist.length > 0 && (
                <ul className="flex flex-col gap-1">
                    {whitelist.map((allowedUrl, idx) => (
                        <WhitelistItem key={`${allowedUrl}-${idx}`}
                                       url={allowedUrl}
                                       index={idx}
                                       onDelete={() => onChange(toSpliced(whitelist, idx, 1))}
                        />
                    ))}
                </ul>
            )}
        </fieldset>
    );
};

interface WhitelistItemProps {
    url: string;
    index: number;
    onDelete: (index: number) => void;
}

const WhitelistItem: React.FC<WhitelistItemProps> = (props) => {
    const { url, index, onDelete } = props;

    const intl = useIntl();

    const id = useId();

    return (
        <li className="flex items-start justify-between gap-2">
            <div id={id}
                className="input-default text-[14px] font-[400] text-black-500"
            >
                {url}
            </div>
            <button className="mt-3"
                    aria-label={intl.formatMessage({ id: "b0006" })}
                    aria-describedby={id}
                    onClick={() => onDelete(index)}
            >
                <DeleteIcon className="size-4 text-point-red" />
            </button>
        </li>
    );
};

export default InstallationInputGroup;
